import React, { useState, useEffect } from 'react';
import { X, Plus, BookOpen, Clock, FolderOpen } from 'lucide-react';
import LearningComponentsService from '../../services/learningComponentsService';
import { coursesService, sessionsService } from '../../services/firestoreService';
import './AddComponentModal.css';

const AddComponentModal = ({ 
  component, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('course'); // 'course', 'session', 'topic'
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadCoursesAndSessions();
    }
  }, [isOpen]);

  const loadCoursesAndSessions = async () => {
    try {
      setLoading(true);
      const [coursesList, sessionsList] = await Promise.all([
        coursesService.getAllCourses(),
        sessionsService.getAllSessions()
      ]);
      setCourses(coursesList || []);
      setSessions(sessionsList || []);
    } catch (error) {
      console.error('Error loading courses and sessions:', error);
      setCourses([]);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleAddComponent = async () => {
    if (!component.id) {
      alert('This component is from the catalog only and is not in the library yet. Add it from Admin (Courses Management or Initialize Components) first, then you can assign it to courses or sessions.');
      return;
    }
    if (selectedItems.length === 0) {
      alert('Please select at least one course, session, or topic');
      return;
    }

    try {
      setLoading(true);
      const addedBy = typeof component.createdBy === 'string' ? component.createdBy : 'current-user';
      for (const itemId of selectedItems) {
        if (selectedLevel === 'course') {
          await LearningComponentsService.addComponentsToCourse(itemId, [component.id], addedBy);
        } else if (selectedLevel === 'session') {
          await LearningComponentsService.addComponentsToSession(itemId, [component.id], addedBy);
        } else if (selectedLevel === 'topic') {
          await LearningComponentsService.addComponentsToTopic(itemId, [component.id], addedBy);
        }
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding component:', error);
      alert('Error adding component. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canAdd = Boolean(component.id);

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSessions = sessions.filter(session => 
    session.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="add-component-modal">
        <div className="modal-header">
          <h3>
            <Plus size={20} />
            Add "{component.name}" to...
          </h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Component Info */}
          <div className="component-info">
            <div className="info-header">
              <h4>{component.name}</h4>
              <span className="component-type">{component.type}</span>
            </div>
            <p>{component.description}</p>
            <div className="info-meta">
              <span className="difficulty">{component.difficulty}</span>
              <span className="time">
                <Clock size={14} /> {component.estimatedTime}m
              </span>
            </div>
          </div>

          {/* Level Selection */}
          <div className="level-selection">
            <h4>Select Level</h4>
            <div className="level-tabs">
              <button
                className={`level-tab ${selectedLevel === 'course' ? 'active' : ''}`}
                onClick={() => setSelectedLevel('course')}
              >
                <BookOpen size={16} />
                Course Level
              </button>
              <button
                className={`level-tab ${selectedLevel === 'session' ? 'active' : ''}`}
                onClick={() => setSelectedLevel('session')}
              >
                <Clock size={16} />
                Session Level
              </button>
              <button
                className={`level-tab ${selectedLevel === 'topic' ? 'active' : ''}`}
                onClick={() => setSelectedLevel('topic')}
              >
                <FolderOpen size={16} />
                Topic Level
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="search-section">
            <input
              type="text"
              placeholder={`Search ${selectedLevel}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Items Selection */}
          <div className="items-selection">
            <h4>
              Select {selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)}s
              <span className="selected-count">({selectedItems.length} selected)</span>
            </h4>
            
            <div className="items-list">
              {selectedLevel === 'course' && filteredCourses.map(course => (
                <div
                  key={course.id}
                  className={`item-card ${selectedItems.includes(course.id) ? 'selected' : ''}`}
                  onClick={() => handleItemSelect(course.id)}
                >
                  <div className="item-content">
                    <h5>{course.title}</h5>
                    <p>Course with {(course.topics || []).length} topics</p>
                  </div>
                  <div className="item-checkbox">
                    <div className={`checkbox ${selectedItems.includes(course.id) ? 'checked' : ''}`}>
                      {selectedItems.includes(course.id) && '✓'}
                    </div>
                  </div>
                </div>
              ))}

              {selectedLevel === 'session' && filteredSessions.map(session => (
                <div
                  key={session.id}
                  className={`item-card ${selectedItems.includes(session.id) ? 'selected' : ''}`}
                  onClick={() => handleItemSelect(session.id)}
                >
                  <div className="item-content">
                    <h5>{session.title}</h5>
                    <p>Part of: {courses.find(c => c.id === session.courseId)?.title || session.courseId || '—'}</p>
                  </div>
                  <div className="item-checkbox">
                    <div className={`checkbox ${selectedItems.includes(session.id) ? 'checked' : ''}`}>
                      {selectedItems.includes(session.id) && '✓'}
                    </div>
                  </div>
                </div>
              ))}

              {selectedLevel === 'topic' && (
                <div className="topics-grid">
                  {filteredCourses.map(course => {
                    const topicList = (course.topics || []).map(t => (typeof t === 'string' ? t : (t && t.name) || ''));
                    return (
                      <div key={course.id} className="course-topics">
                        <h5>{course.title}</h5>
                        {topicList.filter(Boolean).map(topicName => {
                          const itemId = `${course.id}-${topicName}`;
                          return (
                            <div
                              key={itemId}
                              className={`topic-item ${selectedItems.includes(itemId) ? 'selected' : ''}`}
                              onClick={() => handleItemSelect(itemId)}
                            >
                              <span>{topicName}</span>
                              <div className={`checkbox ${selectedItems.includes(itemId) ? 'checked' : ''}`}>
                                {selectedItems.includes(itemId) && '✓'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="add-btn"
            onClick={handleAddComponent}
            disabled={!canAdd || selectedItems.length === 0 || loading}
          >
            {loading ? 'Adding...' : canAdd ? `Add to ${selectedItems.length} ${selectedLevel}${selectedItems.length > 1 ? 's' : ''}` : 'Component has no library ID'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddComponentModal;
